import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  PUBLIC_TIERS,
  tierToDisplayRows,
  annualPriceFor,
  effectiveMonthlyAnnual,
  formatMoney,
  ANNUAL_DISCOUNT_LABEL,
} from '../lib/tiers.js';
import SEO from '../components/SEO.jsx';
import PublicFonts from '../components/PublicFonts.jsx';
import { useAuth } from '../lib/auth.jsx';
import './landing.css';

// Public /pricing page — night-shift theme (shares landing.css's .lp3 system).
// Marketing-only: tier definitions live in lib/tiers.js (single source of truth
// shared with the in-app TierBadge / useTier hook / future enforcement code).

export default function Pricing() {
  // Billing period toggle. 'annual' applies the 2-months-free discount to seat tiers.
  const [billing, setBilling] = useState('monthly');
  // If a vendor admin is already signed in, "Choose plan" should take them to the
  // in-app Billing page (upgrade an existing account), not to Sign-up.
  const { claims } = useAuth();
  const isAdmin = claims?.role === 'vendor_admin' || claims?.role === 'super_admin';
  const ctaTarget = isAdmin ? '/admin/billing' : '/signup';

  useEffect(() => {
    if (!window.location.hash) return;
    requestAnimationFrame(() => {
      const el = document.getElementById(window.location.hash.slice(1));
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  return (
    <div className="lp3">
      <SEO
        title="Pricing"
        description="Simple per-seat pricing for 24/7 service hotline. Free tier for trying it out, Basic AUD $16/seat/month, Enterprise AUD $29/seat/month — or pay annually and get 2 months free. No credit card during pilot."
        path="/pricing"
      />
      <PublicFonts />

      <nav className="lp3-nav">
        <div className="lp3-nav-in">
          <Link to="/" className="lp3-brand" aria-label="Giraops home">
            <span>GIRA<em>OPS</em></span>
          </Link>
          <div className="lp3-nav-links">
            <Link to="/#how">How it works</Link>
            <Link to="/#who">Who it's for</Link>
            <Link to="/pricing" className="is-active">Pricing</Link>
            <Link to="/login">Sign in</Link>
            <Link to="/signup" className="lp3-nav-cta">Get early access</Link>
          </div>
        </div>
      </nav>

      <header className="lp3-pagehead">
        <div className="lp3-wrap">
          <p className="lp3-log">RATE CARD · NO TICKETS-PER-CALL GOTCHAS</p>
          <h2 className="lp3-h2">Simple per-seat pricing. <em>Tickets unlimited.</em></h2>
          <p className="lp3-sec-sub" style={{ marginBottom: 0 }}>
            All tiers free during the early-access pilot. Pricing kicks in when you graduate to
            your first paid month. Pay annually and get <strong style={{ color: 'var(--paper)' }}>{ANNUAL_DISCOUNT_LABEL}</strong>.
          </p>
          <BillingToggle billing={billing} onChange={setBilling} />
        </div>
      </header>

      <section className="lp3-sec" style={{ paddingTop: 24 }}>
        <div className="lp3-wrap">
          <div className="lp3-price-grid">
            {PUBLIC_TIERS.map((tier) => (
              <TierCard key={tier.id} tier={tier} billing={billing} ctaTarget={ctaTarget} />
            ))}
          </div>
          <p className="lp3-price-foot">
            Prices in AUD, ex-GST. Per-seat means per <strong style={{ color: 'var(--paper)' }}>responder</strong> +
            per <strong style={{ color: 'var(--paper)' }}>administrator</strong> — customers (the people raising
            tickets) are always free regardless of how many you have. Annual plans are billed once
            a year for the price of 10 months.
          </p>
        </div>
      </section>

      {/* === AI ADD-ON === */}
      <section className="lp3-sec lp3-sec-alt" id="ai-addon" style={{ paddingTop: 40, paddingBottom: 40 }}>
        <div className="lp3-wrap" style={{ maxWidth: 860 }}>
          <div className="lp3-panel lp3-panel-ai">
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap' }}>
              <h2 className="lp3-h2" style={{ fontSize: 30 }}>AI add-on</h2>
              <span className="lp3-ai-chip">+$15/MONTH · OPTIONAL</span>
            </div>
            <p style={{ marginTop: 12, fontSize: 15.5, color: 'var(--dim)' }}>
              Turn on AI to give every requester a quick triage chat before dispatch and your techs
              an in-app assistant grounded in the manuals you upload. Single vendor-level toggle —
              works for all your seats. No tier change required. Turn it on or off anytime from Settings.
            </p>
            <ul>
              <li><strong>Requester triage chat:</strong> Claude asks 4-6 clarifying questions before dispatch, attaches a clean summary to every ticket.</li>
              <li><strong>Responder tech assistant:</strong> on-site techs ask the AI questions grounded in your uploaded manuals — fault codes, torque specs, procedure references.</li>
              <li><strong>Vendor playbook editor:</strong> tell the AI what to ask per skill category. Safety rails are baked in regardless.</li>
              <li><strong>Knowledge base:</strong> drop in PDF manuals, the AI quotes them in answers.</li>
            </ul>
            <p style={{ marginTop: 14, fontSize: 13, color: 'var(--dim)' }}>
              The AI add-on is billed monthly at $15/month — the annual discount applies to seats only.
              You can opt in at signup or later from Settings. Same pilot terms as seat pricing
              (free during pilot, 30 days' notice before any paid invoice).
            </p>
          </div>
        </div>
      </section>

      {/* === FAQ === */}
      <section className="lp3-sec" id="faq" style={{ paddingTop: 56 }}>
        <div className="lp3-wrap" style={{ maxWidth: 960 }}>
          <p className="lp3-log">STILL UP · COMMON QUESTIONS</p>
          <h2 className="lp3-h2">Common questions</h2>

          <div className="lp3-faq">
            <div className="lp3-faq-item">
              <h3>How does annual billing work?</h3>
              <p>Pick <strong>Annual</strong> above and you pay for 10 months up front instead of 12 — so you get
              <strong> {ANNUAL_DISCOUNT_LABEL}</strong> (~17% off). Basic works out to {formatMoney(annualPriceFor({ monthlyPrice: 16 }))}/seat/year
              and Enterprise {formatMoney(annualPriceFor({ monthlyPrice: 29 }))}/seat/year. The AI add-on stays at $15/month either way.</p>
            </div>

            <div className="lp3-faq-item">
              <h3>What's a "seat"?</h3>
              <p>Anyone who can <em>receive</em> a ticket: responders + administrators. Requesters (the people raising
              tickets) are unlimited and always free.</p>
            </div>

            <div className="lp3-faq-item">
              <h3>Are there limits on how many tickets I can take?</h3>
              <p>No — tickets are <strong>unlimited on every tier, including Free</strong>. We never cap or
              charge by ticket volume; a 24/7 hotline shouldn't stop working mid-month. Tiers differ by how
              many <strong>seats</strong> (responders + administrators) you need and which features you want.</p>
            </div>

            <div className="lp3-faq-item">
              <h3>Why is Free so restrictive?</h3>
              <p>Honest answer: a 24/7 hotline that goes down is brand-damaging for you. Free is meant to validate
              that Giraops fits your workflow — not to run your business on long-term. Basic is the real entry tier
              for a working service vendor.</p>
            </div>

            <div className="lp3-faq-item">
              <h3>What's the difference between Basic and Enterprise availability?</h3>
              <p><strong>Basic</strong> = responders mark themselves available / unavailable on a simple calendar.
              <strong> Enterprise</strong> adds the full coverage-gap detection (red/yellow/green grid showing skill
              coverage 28 days ahead), automated sick-day cover-request workflow, and weekly coverage email digests.</p>
            </div>

            <div className="lp3-faq-item">
              <h3>Do you charge for SMS and voice calls separately?</h3>
              <p>No — they're included in Basic and Enterprise. We absorb the carrier costs. (Excessive use will
              prompt a fair-use conversation, but normal pilot/SMB volumes are well within reason.)</p>
            </div>

            <div className="lp3-faq-item">
              <h3>What's in API access (Enterprise)?</h3>
              <p>Read tickets / push status updates / sync to your existing CRM or FSM. Useful if you're already
              running ServiceTitan, simPRO, ServiceM8 etc. and want Giraops as the after-hours overlay rather than
              a replacement.</p>
            </div>

            <div className="lp3-faq-item">
              <h3>How does the pilot work?</h3>
              <p>All tiers free until you and we both feel ready to formalise. No credit card during the pilot.
              We'll give you 30 days' notice before any plan goes paid. Goal: prove value to you first,
              monetise second.</p>
            </div>
          </div>
        </div>
      </section>

      <footer className="lp3-footer">
        <div className="lp3-footer-in">
          <div><strong style={{ color: 'var(--paper)' }}>Giraops</strong> — 24/7 service hotline · Built in Australia · © 2026</div>
          <div className="lp3-footer-links">
            <Link to="/">Home</Link>
            <a href="/compare/giraops-vs-signl4">vs SIGNL4</a>
            <a href="mailto:support.proftek@gmail.com">Contact</a>
            <Link to="/login">Sign in</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ---- Billing toggle -------------------------------------------------------------

function BillingToggle({ billing, onChange }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <div className="lp3-toggle" role="group" aria-label="Billing period">
        <button type="button" className={billing === 'monthly' ? 'on' : ''} aria-pressed={billing === 'monthly'} onClick={() => onChange('monthly')}>
          Monthly
        </button>
        <button type="button" className={billing === 'annual' ? 'on' : ''} aria-pressed={billing === 'annual'} onClick={() => onChange('annual')}>
          Annual
          <span className="lp3-save">{ANNUAL_DISCOUNT_LABEL}</span>
        </button>
      </div>
    </div>
  );
}

// ---- TierCard subcomponent ------------------------------------------------------

function TierCard({ tier, billing, ctaTarget }) {
  // Derive display rows from the canonical tier object — keeps Pricing.jsx in lockstep
  // with the in-app tier resolver. Edit lib/tiers.js to change anything tier-related.
  const featureRows = tierToDisplayRows(tier);
  const isPaid = (tier.monthlyPrice || 0) > 0;
  const showAnnual = isPaid && billing === 'annual';

  return (
    <div className={`lp3-price-card${tier.accent ? ' is-accent' : ''}`}>
      {tier.accent && <div className="lp3-price-badge">Most popular</div>}
      <div>
        <div className="lp3-price-name">{tier.name}</div>
        <div className="lp3-price-tagline">{tier.tagline}</div>
      </div>
      <div>
        {showAnnual ? (
          <>
            <span className="lp3-price-num">{formatMoney(annualPriceFor(tier))}</span>
            <span className="lp3-price-sub">per seat / year</span>
          </>
        ) : (
          <>
            <span className="lp3-price-num">{tier.price}</span>
            <span className="lp3-price-sub">{tier.priceSub}</span>
          </>
        )}
        {showAnnual && (
          <div className="lp3-price-note">
            {formatMoney(effectiveMonthlyAnnual(tier))}/seat per month, billed annually
            <br />
            <span className="save">Save {formatMoney(tier.monthlyPrice * 2)}/seat per year</span>
          </div>
        )}
        {isPaid && !showAnnual && (
          <div className="lp3-price-note">
            or {formatMoney(annualPriceFor(tier))}/seat/year billed annually
          </div>
        )}
      </div>
      <Link
        to={ctaTarget || '/signup'}
        className={`lp3-btn ${tier.accent ? 'lp3-btn-primary' : 'lp3-btn-ghost'}`}
        style={{ width: '100%', textAlign: 'center' }}
      >
        {tier.ctaLabel}
      </Link>
      <ul>
        {featureRows.map((f) => (
          <li key={f.label} className={f.value === false ? 'off' : ''}>
            <span className="mark">{f.value === false ? '—' : '✓'}</span>
            <span>{f.label}</span>
            {typeof f.value === 'string' && <span className="val">{f.value}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}
